import React from "react";
import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import Engagement from "../apiResources/Engagement";
import { SubmitButton, TextareaInputField } from "./library/Form";
import { useRecruiterUser } from "../api/user";
import Loader from "./library/Loader";
import { useRecruiterPreEngagementCheck } from "../api/engagements";
import Alert from "./library/Alert";

type JobEngagementFormProps = {
  jobId: string;
  employerId: string;
  hasCustomTerms: boolean;
  onSuccess: () => void;
};

type JobEngagementFormValues = {
  message: string;
  hasConfirmedCustomTerms: boolean;
};

const JobEngagementForm = (props: JobEngagementFormProps) => {
  const { jobId, employerId, hasCustomTerms, onSuccess } = props;

  const { data: recruiter, isLoading: loadingUser } = useRecruiterUser();
  const recruiterId = recruiter?.companyId;

  const {
    data: check,
    isLoading: loadingCheck,
    isError,
  } = useRecruiterPreEngagementCheck(
    {
      jobId,
      recruiterId,
    },
    { enabled: !!recruiterId }
  );

  if (loadingUser || loadingCheck) {
    return <Loader inline />;
  }

  if (isError) {
    return (
      <Alert status="error">
        Something went wrong with checking whether you can engage on this role.
        Please contact your account manager for assistance.
      </Alert>
    );
  }

  if (!check.validSector) {
    return (
      <Alert status="warning">
        The sector this job falls under is not currently part of your plan.
        Please contact your account manager to discuss.
      </Alert>
    );
  }

  if (!check.enoughCredits) {
    return (
      <Alert status="warning">
        Your account does not have enough engagement credits to engage on this
        job. Please contact your account manager to discuss.
      </Alert>
    );
  }

  return (
    <Formik<JobEngagementFormValues>
      initialValues={{ message: "", hasConfirmedCustomTerms: false }}
      onSubmit={async (values, helpers) => {
        const engagement = new Engagement({
          jobId,
          employerId,
          recruiterId: recruiter.companyId,
          recruiterUserId: recruiter.id,
          message: values.message,
          hasConfirmedCustomTerms: values.hasConfirmedCustomTerms,
        });

        const success = await engagement.save();

        if (success) {
          return onSuccess();
        }

        helpers.setErrors({
          message:
            engagement.errors.message?.fullMessage ||
            "Something went wrong, please contact your account manager",
        });
      }}
      validationSchema={Yup.object({
        message: Yup.string()
          .required("Please enter a message")
          .min(70, "Please enter at least 70 characters")
          .max(350, "Must not be longer than 350 characters"),
      })}
    >
      <Form id="job-engagement-form">
        <TextareaInputField
          label="Engagement message"
          placeholder="This message will be sent to the employer along with your request to work on this job."
          name="message"
          maxLength={350}
        />

        {hasCustomTerms && (
          <div className="form-group">
            <div className="checkbox">
              <p className="terms-label">
                This employer utilises their own Terms of Business. Please
                confirm you have read and agree to the employer’s specific Terms
                of Business before engaging as they differ from Hiring
                Hub&apos;s standard terms:
              </p>
              <br />
              <div className="checkbox-wrapper">
                <label htmlFor="hasConfirmedCustomTerms">
                  <Field name="hasConfirmedCustomTerms" type="checkbox" />
                  Agree to employer’s specific Terms of Business?
                </label>
              </div>
            </div>
          </div>
        )}

        <SubmitButton>Engage</SubmitButton>
      </Form>
    </Formik>
  );
};

export default JobEngagementForm;
